/* Local browser history, never sent to analytics.
 * Fresh public product data; no order history, customer ID or cart mutation.
 * Respect Shopify preference permission; fail closed if API/storage unavailable.
 */
(() => {
  const source = document.getElementById('dpk-view-history');
  if (!source || source.dataset.bound) return;
  source.dataset.bound = 'true';
  const KEY = 'dpk_recent_products_v1';
  const TTL = 14 * 24 * 60 * 60 * 1000;
  const current = source.dataset.current;
  const root = new URL(source.dataset.root || '/', location.origin);
  const section = document.querySelector('[data-dpk-recent]');
  const list = section?.querySelector('[data-recent-list]');
  let generation = 0;
  let observer;
  const eligible = handle => typeof handle === 'string' && /^[\p{L}\p{N}][\p{L}\p{N}\p{M}\p{S}-]{0,254}$/u.test(handle) && !/kalendarz|adwent/i.test(handle);
  const allowed = () => window.Shopify?.customerPrivacy?.preferencesProcessingAllowed() === true;
  const hide = () => {
    if (section) section.hidden = true;
    if (list) list.replaceChildren();
  };
  const productLink = handle => {
    const url = new URL(root.pathname + 'products/' + encodeURIComponent(handle), location.origin);
    return url.pathname + url.search;
  };
  const element = (tag, cls, text) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  };
  const money = value => new Intl.NumberFormat(source.dataset.locale || 'pl', {
    style:'currency', currency: source.dataset.currency || 'PLN'
  }).format(value / 100);
  function card(product) {
    const item = element('li', 'dpk-recent__card');
    item.dataset.recentHandle = product.handle;
    const href = productLink(product.handle);
    const imageLink = element('a');
    imageLink.href = href;
    imageLink.tabIndex = -1;
    imageLink.setAttribute('aria-hidden', 'true');
    const photo = element('img', 'dpk-recent__image');
    const imageUrl = new URL(product.featured_image, location.origin);
    if (imageUrl.protocol !== 'https:') throw new Error('Invalid product image');
    imageUrl.searchParams.set('width', '160');
    photo.src = imageUrl.href;
    photo.alt = ''; photo.width = 72; photo.height = 88; photo.loading = 'lazy';
    imageLink.append(photo);
    const details = element('div', 'dpk-recent__details');
    const title = element('a', 'dpk-recent__name', product.title);
    title.href = href; title.title = product.title;
    const price = element('p', 'dpk-recent__price', (product.price_varies ? 'Od ' : '') + money(product.price));
    // Return to the product to choose size/grind. Never guess a purchasable variant.
    const link = element('a', 'dpk-recent__link', product.variants.length > 1 || product.type === 'Kawa' ? 'Wybierz opcje' : 'Zobacz produkt');
    link.href = href;
    link.setAttribute('aria-label', link.textContent + ': ' + product.title);
    details.append(title, price, link); item.append(imageLink, details);
    return item;
  }
  function arrows() {
    if (!section || section.hidden) return;
    const overflow = list.scrollWidth > list.clientWidth + 2;
    section.querySelector('[data-recent-arrows]').hidden = !overflow;
    section.querySelector('[data-recent-prev]').disabled = list.scrollLeft < 2;
    section.querySelector('[data-recent-next]').disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;
  }
  async function render(history, run) {
    if (!list || !allowed() || run !== generation) return;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    const results = await Promise.allSettled(history.slice(0, 12).map(async entry => {
      const response = await fetch(root.pathname + 'products/' + encodeURIComponent(entry.handle) + '.js', {
        credentials:'same-origin', signal:controller.signal
      });
      if (!response.ok) throw new Error('Product unavailable');
      const p = await response.json();
      if (p.handle !== entry.handle || !p.available || !p.featured_image || !Number.isFinite(p.price) || !Array.isArray(p.variants)) throw new Error('Product invalid');
      return card(p);
    }));
    clearTimeout(timer);
    if (run !== generation || !allowed()) return;
    const items = results.filter(r => r.status === 'fulfilled').map(r => r.value).slice(0, 6);
    list.replaceChildren(...items);
    section.hidden = items.length === 0;
    section.dataset.state = items.length ? 'ready' : 'no-eligible-history';
    source.dataset.historyCount = String(items.length);
    requestAnimationFrame(arrows);
  }
  function refresh(record = true) {
    const run = ++generation;
    observer?.disconnect();
    hide();
    if (!allowed()) {
      source.dataset.state = 'preferences-not-allowed';
      // A background tab can still hold its previous consent state. A storage
      // notification must never erase a newer tab's history. Explicit refusal
      // and revocation still purge on their own consent/initialization path.
      if (record) try { localStorage.removeItem(KEY); } catch {}
      return;
    }
    let history;
    try {
      const raw = JSON.parse(localStorage.getItem(KEY) || '[]');
      const seen = new Set();
      history = (Array.isArray(raw) ? raw : []).filter(item => {
        if (!item || !eligible(item.handle) || !Number.isFinite(item.at) || item.at > Date.now() || Date.now() - item.at > TTL || seen.has(item.handle)) return false;
        seen.add(item.handle); return item.handle !== current;
      }).sort((a,b) => b.at-a.at).slice(0, 12);
      const updated = eligible(current) ? [{handle:current,at:Date.now()}, ...history].slice(0,12) : history;
      if (record) localStorage.setItem(KEY, JSON.stringify(updated));
      source.dataset.state = history.length ? 'history-available' : 'first-product';
    } catch { source.dataset.state = 'storage-unavailable'; return; }
    if (!list || !history.length) return;
    // Observe the Shopify wrapper, which remains in layout while the empty module is hidden.
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          observer.disconnect(); render(history, run);
        }
      }, {rootMargin:'400px'});
      observer.observe(section.parentElement);
    } else render(history, run);
  }
  section?.querySelector('[data-recent-clear]').addEventListener('click', () => {
    generation++; observer?.disconnect();
    try { localStorage.removeItem(KEY); } catch {}
    hide(); source.dataset.state = 'history-cleared';
  });
  ['prev','next'].forEach(direction => section?.querySelector('[data-recent-' + direction + ']').addEventListener('click', () => {
    list.scrollBy({left:(direction === 'next' ? 1 : -1) * (list.firstElementChild?.getBoundingClientRect().width + 14 || 289),
      behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
  }));
  list?.addEventListener('scroll', arrows, {passive:true});
  window.addEventListener('resize', arrows, {passive:true});
  document.addEventListener('visitorConsentCollected', () => refresh());
  window.addEventListener('storage', event => { if (event.key === KEY) refresh(false); });
  window.addEventListener('pageshow', event => { if (event.persisted) refresh(); });
  if (window.Shopify?.customerPrivacy) refresh();
  else if (window.Shopify?.loadFeatures) {
    const loaded = error => {
      if (!error && window.Shopify?.customerPrivacy) refresh();
      else source.dataset.state = 'privacy-api-unavailable';
    };
    window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.2'}], error => {
      if (!error) loaded(error);
      else {
        // This store also uses the earlier API loader. Both paths keep the same permission gate.
        window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}], loaded);
      }
    });
  }
})();
